import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useTranslation } from "react-i18next"; // Import useTranslation
import Skeleton from "react-loading-skeleton"; // Import Skeleton
import ProductCard from "../common/ProductCard";
import type { TransformedProduct } from "../../types/productApiTypes";

/**
 * Props interface for the RelatedProducts component
 */
interface RelatedProductsProps {
  products: TransformedProduct[];
  isLoading: boolean;
}

/**
 * RelatedProducts component
 * Displays a carousel of related products
 * Pure presentational component that receives products as props
 */
const RelatedProducts: React.FC<RelatedProductsProps> = ({
  products,
  isLoading,
}) => {
  const { t } = useTranslation(); // Initialize useTranslation

  // Don't render the entire section if there are no products and it's not loading.
  // This prevents showing the title skeleton unnecessarily if the API call is skipped.
  if (!isLoading && (!products || products.length === 0)) {
    return null;
  }

  return (
    <div className="related-products">
      {isLoading ? (
        <>
          {/* Wrapper div for centering the skeleton */}
          <div className="related-products-title-skeleton-wrapper">
            <Skeleton
              height={30}
              width={200}
              style={{ marginBottom: "1rem" }}
              className="section-title-skeleton" // Keep existing class if needed
            />
          </div>
          <div className="related-products-loading">
            {/* Keep existing ProductCard skeletons */}
            {[...Array(4)].map((_, index) => (
              <ProductCard.Skeleton key={index} />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Actual title */}
          <h2 className="section-title">{t("product.relatedProducts")}</h2>
          <Swiper
            modules={[Navigation, Pagination]}
            navigation
            pagination={{ clickable: true }}
            spaceBetween={20}
            slidesPerView={1}
            className="related-products-carousel"
            breakpoints={{
              // When screen width is >= 576px
              576: {
                slidesPerView: 2,
              },
              // When screen width is >= 768px
              768: {
                slidesPerView: 3,
              },
              // When screen width is >= 1024px
              1024: {
                slidesPerView: 4,
              },
            }}
          >
            {products.map((product) => (
              <SwiperSlide key={product.id}>
                <ProductCard product={product} viewMode="grid" />
              </SwiperSlide>
            ))}
          </Swiper>
        </>
      )}
    </div>
  );
};

export default RelatedProducts;
